import { RouteShell } from "@/components/route-shell";
import { ProviderChip } from "@/components/ui/provider-chip";
import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";
import {
	ArrowRight,
	CheckCircle2,
	CircleSlash,
	FileText,
	RefreshCw,
	TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

// Human labels for each raw attention reason the server emits. Keeping the
// map tight + falling back to the raw string means a new reason appears in
// the UI without breaking — it just renders as the raw code until wired up.
const REASON_LABELS: Record<string, string> = {
	unmatched: "Unmatched customer",
	zero_employees: "Zero employees",
	zero_charges: "Zero charges",
	duplicate: "Possible duplicate",
	// The server emits these two, not the bare `duplicate` above — see
	// auto-invoice.service.ts. `_billed` means an approved/pushed/skipped
	// invoice already covers the period (blocking); the bare form means only
	// a draft exists.
	duplicate_period_billed: "Already billed this period",
	duplicate_period: "Draft exists for this period",
	no_billing_rules: "No billing rules",
};

export function reasonLabel(reason: string): string {
	return REASON_LABELS[reason] ?? reason;
}

const searchSchema = z.object({
	reason: z.string().optional(),
});

export const Route = createFileRoute("/_app/attention")({
	component: AttentionPageRoute,
	validateSearch: searchSchema,
});

function AttentionPageRoute() {
	return (
		<RouteShell>
			<AttentionPage />
		</RouteShell>
	);
}

function AttentionPage() {
	const search = useSearch({ from: "/_app/attention" });
	const activeReason = search.reason;

	const { data: counts, isLoading: countsLoading } = trpc.extractions.attentionCounts.useQuery(
		undefined,
		{ throwOnError: true },
	);
	const { data: queue, isLoading: queueLoading } = trpc.extractions.attentionQueue.useQuery(
		{ reason: activeReason, page: 1, pageSize: 50 },
		{ throwOnError: true },
	);

	const totalFlagged = counts ? Object.values(counts).reduce((sum, n) => sum + n, 0) : null;

	return (
		<div className="space-y-6" data-testid="attention-page">
			<div>
				<h1 className="text-xl font-semibold text-[#1e1e20]">
					Attention queue
					{totalFlagged !== null ? ` (${totalFlagged})` : ""}
				</h1>
				<p className="mt-1 max-w-2xl text-sm text-[#6b7280]">
					Extractions that couldn't be auto-resolved. Open each to reassign a customer, fix
					extracted numbers, or clear the flag once reviewed.
				</p>
			</div>

			{/* Reason filter chips */}
			<ReasonChips counts={counts} activeReason={activeReason} loading={countsLoading} />

			{/* Queue */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				{queueLoading ? (
					<QueueSkeleton />
				) : !queue || queue.items.length === 0 ? (
					<EmptyState hasFilter={activeReason != null} />
				) : (
					<QueueTable items={queue.items} />
				)}
			</div>
		</div>
	);
}

function ReasonChips({
	counts,
	activeReason,
	loading,
}: {
	counts: Record<string, number> | undefined;
	activeReason: string | undefined;
	loading: boolean;
}) {
	if (loading) {
		return (
			<div className="flex gap-2">
				<div className="h-7 w-24 animate-pulse rounded-full bg-[#f3f4f6]" />
				<div className="h-7 w-28 animate-pulse rounded-full bg-[#f3f4f6]" />
				<div className="h-7 w-20 animate-pulse rounded-full bg-[#f3f4f6]" />
			</div>
		);
	}
	if (!counts || Object.keys(counts).length === 0) return null;

	const total = Object.values(counts).reduce((sum, n) => sum + n, 0);

	return (
		<div className="flex flex-wrap gap-2">
			<Link
				to="/attention"
				className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
					activeReason == null
						? "border-[#4f46e5] bg-[#eef2ff] text-[#4f46e5]"
						: "border-[#e5e7eb] bg-white text-[#6b7280] hover:border-[#4f46e5] hover:text-[#4f46e5]"
				}`}
			>
				All
				<span className="tabular-nums text-[#9ca3af]">{total}</span>
			</Link>
			{Object.entries(counts)
				.sort((a, b) => b[1] - a[1])
				.map(([reason, count]) => (
					<Link
						key={reason}
						to="/attention"
						search={{ reason }}
						className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
							activeReason === reason
								? "border-[#4f46e5] bg-[#eef2ff] text-[#4f46e5]"
								: "border-[#e5e7eb] bg-white text-[#6b7280] hover:border-[#4f46e5] hover:text-[#4f46e5]"
						}`}
					>
						{reasonLabel(reason)}
						<span className="tabular-nums text-[#9ca3af]">{count}</span>
					</Link>
				))}
		</div>
	);
}

type QueueItem = {
	id: string;
	documentId: string;
	fileName: string | null;
	payrollSystem: string;
	sourceCharges: number;
	employeeCount: number;
	customerId: string | null;
	customerName: string | null;
	attentionReason: string | null;
	extractedName: string | null;
};

function QueueTable({ items }: { items: QueueItem[] }) {
	const utils = trpc.useUtils();
	const recheck = trpc.extractions.resolveAttention.useMutation({
		onSuccess: (result) => {
			utils.extractions.attentionQueue.invalidate();
			utils.extractions.attentionCounts.invalidate();
			if (result && result.autoInvoiced > 0) {
				toast.success("Invoice generated");
			} else if (result?.stillFlagged) {
				// Re-check re-runs the same eligibility checks, so an unchanged
				// condition comes straight back. Saying "Re-checked" here read
				// as success and left people re-clicking a row that could never
				// clear this way — Dismiss is the action that removes it.
				toast.warning(`Still flagged: ${reasonLabel(result.stillFlagged)}`, {
					description: "Nothing changed underneath. Use Dismiss if this was handled elsewhere.",
				});
			} else {
				toast.success("Re-checked");
			}
		},
		onError: (err) => toast.error(`Recheck failed: ${err.message}`),
	});

	const dismiss = trpc.extractions.dismissAttention.useMutation({
		onSuccess: () => {
			utils.extractions.attentionQueue.invalidate();
			utils.extractions.attentionCounts.invalidate();
			toast.success("Dismissed");
		},
		onError: (err) => toast.error(`Dismiss failed: ${err.message}`),
	});

	return (
		<table className="w-full text-sm">
			<thead>
				<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
					<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Extracted name
					</th>
					<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						System
					</th>
					<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Charges
					</th>
					<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Customer
					</th>
					<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Reason
					</th>
					<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Source document
					</th>
					<th className="w-20 px-2 py-2.5" />
				</tr>
			</thead>
			<tbody>
				{items.map((item) => (
					<tr
						key={item.id}
						className="border-b border-[#f3f4f6] transition-colors duration-150 last:border-0 hover:bg-[#f8f9fb]"
					>
						<td className="px-4 py-3 font-medium text-[#1e1e20]">
							{item.extractedName ?? <span className="text-[#9ca3af]">—</span>}
						</td>
						<td className="px-4 py-3">
							<ProviderChip
								system={
									item.payrollSystem as
										| "ADP"
										| "Paylocity"
										| "SurePayroll"
										| "Gusto"
										| (string & {})
								}
							/>
						</td>
						<td className="px-4 py-3 text-right tabular-nums text-[#1e1e20]">
							{formatDollars(item.sourceCharges)}
						</td>
						<td className="px-4 py-3">
							{item.customerId && item.customerName ? (
								<Link
									to="/customers/$customerId"
									params={{ customerId: item.customerId }}
									className="text-[#4f46e5] hover:underline"
								>
									{item.customerName}
								</Link>
							) : (
								<span className="text-xs text-[#9ca3af]">Unassigned</span>
							)}
						</td>
						<td className="px-4 py-3">
							<span className="inline-flex items-center gap-1 rounded bg-[#fffbeb] px-1.5 py-0.5 text-xs font-medium text-[#d97706]">
								<TriangleAlert className="h-3 w-3" strokeWidth={2} />
								{item.attentionReason ? reasonLabel(item.attentionReason) : "flagged"}
							</span>
						</td>
						<td className="px-4 py-3">
							<Link
								to="/documents/$documentId"
								params={{ documentId: item.documentId }}
								className="inline-flex items-center gap-1.5 text-[#4f46e5] hover:underline"
							>
								<FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
								<span className="truncate max-w-[20ch]">{item.fileName ?? "source document"}</span>
							</Link>
						</td>
						<td className="px-2 py-3">
							<div className="flex items-center justify-end gap-1">
								<button
									type="button"
									onClick={() => recheck.mutate({ extractionId: item.id })}
									disabled={recheck.isPending && recheck.variables?.extractionId === item.id}
									className="inline-flex items-center rounded p-1 text-[#9ca3af] hover:bg-[#eef2ff] hover:text-[#4f46e5] disabled:opacity-50"
									aria-label="Recheck extraction"
									title="Re-check this extraction"
								>
									<RefreshCw
										className={`h-4 w-4 ${
											recheck.isPending && recheck.variables?.extractionId === item.id
												? "animate-spin"
												: ""
										}`}
										strokeWidth={2}
									/>
								</button>
								<button
									type="button"
									onClick={() => dismiss.mutate({ extractionId: item.id })}
									disabled={dismiss.isPending && dismiss.variables?.extractionId === item.id}
									className="inline-flex items-center rounded p-1 text-[#9ca3af] hover:bg-[#fef2f2] hover:text-[#dc2626] disabled:opacity-50"
									aria-label="Dismiss extraction"
									title="Already handled outside the app — remove from this queue"
								>
									<CircleSlash className="h-4 w-4" strokeWidth={2} />
								</button>
								<Link
									to="/documents/$documentId"
									params={{ documentId: item.documentId }}
									className="inline-flex items-center rounded p-1 text-[#9ca3af] hover:bg-[#eef2ff] hover:text-[#4f46e5]"
									aria-label="Open source document"
								>
									<ArrowRight className="h-4 w-4" strokeWidth={2} />
								</Link>
							</div>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
	return (
		<div className="flex flex-col items-center py-16 text-center">
			<CheckCircle2 className="h-10 w-10 text-[#16a34a]" strokeWidth={1.5} />
			<p className="mt-3 text-sm font-semibold text-[#1e1e20]">
				{hasFilter ? "Nothing in this bucket" : "All clear"}
			</p>
			<p className="mt-1 max-w-sm text-xs text-[#6b7280]">
				{hasFilter
					? "No extractions match this reason. Try another filter or clear to see everything."
					: "No extractions need attention right now. New flags will appear here as documents are processed."}
			</p>
			{hasFilter && (
				<Link to="/attention" className="mt-4 text-xs font-medium text-[#4f46e5] hover:underline">
					Clear filter
				</Link>
			)}
		</div>
	);
}

const SKELETON_KEYS = ["s1", "s2", "s3", "s4"];

function QueueSkeleton() {
	return (
		<div className="divide-y divide-[#f3f4f6]">
			{SKELETON_KEYS.map((k) => (
				<div key={k} className="h-14 animate-pulse bg-[#f8f9fb]" />
			))}
		</div>
	);
}
